import { FC, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { DashboardStatCard } from "@/features/dashboard/components/DashboardStatCard";
import { MatchingResultsModal } from "@/features/dashboard/components/MatchingResultsModal/MatchingResultsModal";
import { TMatchingResultsMetric } from "@/features/dashboard/types/matching-results";
import { background } from "@/theme/colors";
import { TMonthlyCountCaseMatchesSummary } from "@/api/services/main/dashboard/dtos/get-count-summary.dto";
import { TMonthlyTarget } from "@/api/services/main/dashboard/dtos/monthly-target.dto";
import { useVisible } from "@/hooks/useVisible";

type TDashboardMonthlyCaseMatchesStatisticsProps = {
  title?: string;
  // Month this row represents (used by modal to fetch the correct data).
  targetMonth: Dayjs;
  currentCaseMatchesSummaryData?: TMonthlyCountCaseMatchesSummary;
  prevCaseMatchesSummaryData?: TMonthlyCountCaseMatchesSummary;
  monthlyTargetData?: TMonthlyTarget;
  // 新規登録人材数 comes from the count-summary endpoint, not the case-matches one.
  currentNewTalents?: number;
  prevNewTalents?: number;
};

export const DashboardMonthlyCaseMatchesStatistics: FC<TDashboardMonthlyCaseMatchesStatisticsProps> = ({
  title,
  targetMonth,
  currentCaseMatchesSummaryData,
  prevCaseMatchesSummaryData,
  monthlyTargetData,
  currentNewTalents,
  prevNewTalents,
}) => {
  const currentBeforeProposal = currentCaseMatchesSummaryData?.beforeProposal ?? 0;
  const currentProposing = currentCaseMatchesSummaryData?.proposing ?? 0;
  const currentInterview = currentCaseMatchesSummaryData?.underSelection ?? 0;
  const currentOffer = currentCaseMatchesSummaryData?.offer ?? 0;
  const currentAccept = currentCaseMatchesSummaryData?.offerAccepted ?? 0;

  const prevBeforeProposal = prevCaseMatchesSummaryData?.beforeProposal ?? 0;
  const prevProposing = prevCaseMatchesSummaryData?.proposing ?? 0;
  const prevInterview = prevCaseMatchesSummaryData?.underSelection ?? 0;
  const prevOffer = prevCaseMatchesSummaryData?.offer ?? 0;
  const prevAccept = prevCaseMatchesSummaryData?.offerAccepted ?? 0;

  // 転換率 computed by the BE shared helper (funnel: next stage / previous stage).
  const currentBeforeProposalRate = currentCaseMatchesSummaryData?.rates.beforeProposalRate ?? 0;
  const currentProposalRate = currentCaseMatchesSummaryData?.rates.proposalRate ?? 0;
  const currentInterviewRate = currentCaseMatchesSummaryData?.rates.interviewRate ?? 0;
  const currentOfferRate = currentCaseMatchesSummaryData?.rates.offerRate ?? 0;
  const currentAcceptRate = currentCaseMatchesSummaryData?.rates.acceptRate ?? 0;

  const prevBeforeProposalRate = prevCaseMatchesSummaryData?.rates.beforeProposalRate ?? 0;
  const prevProposalRate = prevCaseMatchesSummaryData?.rates.proposalRate ?? 0;
  const prevInterviewRate = prevCaseMatchesSummaryData?.rates.interviewRate ?? 0;
  const prevOfferRate = prevCaseMatchesSummaryData?.rates.offerRate ?? 0;
  const prevAcceptRate = prevCaseMatchesSummaryData?.rates.acceptRate ?? 0;

  const currentTalents = currentNewTalents ?? 0;
  const prevTalents = prevNewTalents ?? 0;

  // Modal state: single modal shared across the 4 clickable cards, metric selected on click.
  const modal = useVisible();
  const [activeMetric, setActiveMetric] = useState<TMatchingResultsMetric | null>(null);

  const handleCardClick = (metric: TMatchingResultsMetric) => {
    setActiveMetric(metric);
    modal.open();
  };

  const handleModalClose = () => {
    modal.close();
    // Keep activeMetric until modal fully closes (prevents flicker); reset on next open anyway.
  };

  return (
    <Box
      mt={2}
      sx={{
        p: "16px 32px",
        border: `1px solid ${background.disable}`,
        borderRadius: "8px",
        backgroundColor: background.white,
      }}
    >
      {title && (
        <Typography component="p" variant="sub16Semi" mb={2}>
          {title}
        </Typography>
      )}
      <Grid container spacing={5}>
        <Grid item xs>
          <DashboardStatCard
            label="新規登録人材数"
            current={currentTalents}
            previous={prevTalents}
            target={monthlyTargetData?.targetTalents}
            unit="人"
            isTarget={true}
            showIconArrowRight={true}
            tooltipInfo="人材の作成日時が指定年月内のものをカウント"
          />
        </Grid>
        <Grid item xs>
          <DashboardStatCard
            label="推薦前（推薦前転換率）"
            current={currentBeforeProposal}
            previous={prevBeforeProposal}
            target={monthlyTargetData?.targetMatchingBeforeProposals}
            unit="件"
            isTarget={true}
            rateCurrent={currentBeforeProposalRate}
            ratePrevious={prevBeforeProposalRate}
            rateTarget={monthlyTargetData?.targetMatchingBeforeProposalRate}
            showIconArrowRight={true}
            tooltipInfo="推薦前日が指定年月であるマッチング件数です。"
          />
        </Grid>
        <Grid item xs>
          <DashboardStatCard
            label="推薦数（推薦転換率）"
            current={currentProposing}
            previous={prevProposing}
            target={monthlyTargetData?.targetMatchingRecommendations}
            unit="件"
            isTarget={true}
            onCurrentClick={() => handleCardClick("proposing")}
            rateCurrent={currentProposalRate}
            ratePrevious={prevProposalRate}
            rateTarget={monthlyTargetData?.targetMatchingRecommendationRate}
            showIconArrowRight={true}
            tooltipInfo="推薦日が指定年月であるマッチング件数です。"
          />
        </Grid>
        <Grid item xs>
          <DashboardStatCard
            label="面接数（面接転換率）"
            current={currentInterview}
            previous={prevInterview}
            target={monthlyTargetData?.targetMatchingInterviews}
            unit="件"
            isTarget={true}
            onCurrentClick={() => handleCardClick("interview")}
            rateCurrent={currentInterviewRate}
            ratePrevious={prevInterviewRate}
            rateTarget={monthlyTargetData?.targetMatchingInterviewRate}
            showIconArrowRight={true}
            tooltipInfo="面接日が指定年月であるマッチング件数です。"
          />
        </Grid>
        <Grid item xs>
          <DashboardStatCard
            label="内定数（内定転換率）"
            current={currentOffer}
            previous={prevOffer}
            target={monthlyTargetData?.targetMatchingOffers}
            unit="件"
            isTarget={true}
            onCurrentClick={() => handleCardClick("offer")}
            rateCurrent={currentOfferRate}
            ratePrevious={prevOfferRate}
            rateTarget={monthlyTargetData?.targetMatchingOfferRate}
            showIconArrowRight={true}
            tooltipInfo="内定日が指定年月であるマッチング件数です。"
          />
        </Grid>
        <Grid item xs>
          <DashboardStatCard
            label="内定受諾数（内定受諾転換率）"
            current={currentAccept}
            previous={prevAccept}
            target={monthlyTargetData?.targetMatchingAcceptances}
            unit="件"
            isTarget={true}
            onCurrentClick={() => handleCardClick("accept")}
            rateCurrent={currentAcceptRate}
            ratePrevious={prevAcceptRate}
            rateTarget={monthlyTargetData?.targetMatchingAcceptanceRate}
            tooltipInfo="内定受諾日が指定年月であるマッチング件数です。"
          />
        </Grid>
      </Grid>

      {activeMetric && <MatchingResultsModal isOpen={modal.visible} onClose={handleModalClose} metric={activeMetric} targetMonth={targetMonth} />}
    </Box>
  );
};
